import { Button, Container, Input } from 'components/atoms';
import StatisticsGrid from 'components/atoms/statistics/StatisticsGrid';
import { FormInput } from 'components/molecules';
import StatisticsListContainer from 'components/molecules/statistics/StatisticsListContainer';
import { TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { Form, FormikProvider, useFormik } from 'formik';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import {
  PiCalendar,
  PiChartBar,
  PiIdentificationCard,
  PiInfoBold,
  PiNavigationArrow,
  PiVinylRecord,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import {
  useGetEarnings,
  useGetSalesChannelsWithProviderEarnings,
  useGetTopLabels,
  useGetTopProviders,
  useGetTopSalesChannels,
  useGetTopTracksEarnings,
} from 'services/statistics';

import { useGetTrackEarnings } from 'services/statistics/track';
import {
  ISalesChannelWithProviderEarning,
  IStatisticsQuery,
  ITopTrackEarnings,
} from 'services/statistics/types';
import { useTokenData } from 'store/state';

import { formatCurrency } from 'utils/global/currency.utils';

import { getSettlementPeriodName } from 'utils/global/name.utils';


export interface IHomeStatistics {
  labels: IStatisticsQuery;
  earnings: IStatisticsQuery;
  tracks: IStatisticsQuery;
  track: ITopTrackEarnings;
  salesChannels: IStatisticsQuery;
  providers: IStatisticsQuery;
}

const queryInitialValues: IStatisticsQuery = {
  endSettlementPeriodId: '',
  startSettlementPeriodId: '',
};

const initialValues: IHomeStatistics = {
  labels: queryInitialValues,
  providers: queryInitialValues,
  salesChannels: queryInitialValues,
  earnings: queryInitialValues,
  tracks: queryInitialValues,
  track: { amountEarned: 0, id: '', isrc: '', quantity: 0, title: '' },
};

const Statistics = () => {
  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: () => {},
    enableReinitialize: true,
  });



  const settlementPeriod = useGetSettlementLightList();

  const options =
    settlementPeriod.data?.map(p => ({
      label: getSettlementPeriodName(p.settlementPeriodName),
      value: p.id,
    })) ?? [];



  const topSalesChannels = useGetTopSalesChannels(formik.values.salesChannels);

  const mappedTopSalesChannels = useMemo(() => {
    const sum =
      topSalesChannels.data?.reduce(
        (acc, earning) => acc + earning.allAmount,
        0
      ) ?? 0;

    const percentToOthers = 0.02;

    const filtered =
      topSalesChannels.data?.filter(s => s.allAmount > sum * percentToOthers) ??
      [];

    const otherElements =
      topSalesChannels.data?.filter(
        s => s.allAmount <= sum * percentToOthers
      ) ?? [];

    const otherSum =
      otherElements.reduce((acc, earning) => acc + earning.allAmount, 0) ?? 0;

    return [
      ...filtered,
      ...(!!otherElements.length
        ? [
            {
              id: 'others',
              name: t('statistics.others'),
              logoImageUrl: '',
              allAmount: otherSum,
            },
          ]
        : []),
    ];
  }, [topSalesChannels.data]);

  const topProviders = useGetTopProviders(formik.values.providers);
  const earnings = useGetEarnings(formik.values.earnings);
  const topLabels = useGetTopLabels(formik.values.labels);
  const topTracks = useGetTopTracksEarnings(formik.values.tracks);

  const trackEarnings = useGetTrackEarnings(
    {
      ...formik.values.tracks,
      trackId: formik.values.track.id,
    },
    !!formik.values.tracks.endSettlementPeriodId &&
      !!formik.values.tracks.startSettlementPeriodId &&
      !!formik.values.track
  );

  const mappedTrackEarnings = useMemo(() => {
    return trackEarnings.data
      ?.map(e => ({
        name:
          getSettlementPeriodName(
            settlementPeriod.data?.find(s => s.id === e.settlementPeriodId)
              ?.settlementPeriodName
          ) ?? '',
        value: e.earnings ?? 0,
      }));
  }, [
    trackEarnings.data,
    settlementPeriod.data,
    formik.values.tracks.startSettlementPeriodId,
    formik.values.tracks.endSettlementPeriodId,
    formik.values.track.id,
    undefined,
  ]);

  const totalEarnings = useMemo<number>(
    () =>
      earnings.data?.reduce((acc, earning) => acc + earning.earnedAmount, 0) ??
      0,
    [earnings.data, settlementPeriod.data]
  );

  const mappedEarnings = useMemo(() => {
    return earnings.data?.map(e => ({
      name: getSettlementPeriodName(e.settlementPeriod) ?? '',
      value: e.earnedAmount ?? 0,
    }));
  }, [earnings.data, settlementPeriod.data]);

  const salesChannelsWithProviders = useGetSalesChannelsWithProviderEarnings(
    formik.values.providers
  );

  interface IMappedSalesChannel extends Record<string, any> {
    id: string;
    name: string;
    providers: ISalesChannelWithProviderEarning[];
  }

  const mappedSalesChannelsWithProviders = useMemo(
    () =>
      salesChannelsWithProviders.data?.reduce<IMappedSalesChannel[]>(
        (
          acc: IMappedSalesChannel[],
          item: ISalesChannelWithProviderEarning
        ) => {
          if (!acc.length) {
            return [
              {
                id: item.id,
                name: item.name,
                providers: [item],
                [item.providerId]: item.allAmount,
              },
            ];
          }

          const index = acc.findIndex(s => s.id === item.id);

          if (index !== -1) {
            const salesChannels = acc.map((ac, key) =>
              ac[item.providerId] === undefined
                ? {
                    ...ac,
                    providers: [...ac.providers, item],
                    [item.providerId]: ac.id === item.id ? item.allAmount : 0,
                  }
                : ac
            );

            return salesChannels;
          }

          if (index === -1) {
            const salesChannels = [
              ...acc.map(ac =>
                ac[item.providerId] === undefined
                  ? {
                      ...ac,
                      providers: [...ac.providers, item],
                      [item.providerId]: ac.id === item.id ? item.allAmount : 0,
                    }
                  : ac
              ),
              {
                ...Object.fromEntries(Object.keys(acc[0]).map(key => [key, 0])),
                id: item.id,
                name: item.name,
                providers: [...acc[0].providers, item],
                [item.providerId]: item.allAmount,
              },
            ];
            return salesChannels;
          }

          return acc;
        },
        []
      ) ?? [],
    [salesChannelsWithProviders.data, settlementPeriod.data]
  );

  useEffect(() => {
    if (settlementPeriod.data?.length) {
      const end = settlementPeriod.data[0];
      const start = settlementPeriod.data[settlementPeriod.data.length - 1];

      const query: IStatisticsQuery = {
        endSettlementPeriodId: end.id,
        startSettlementPeriodId: start.id,
      };

      formik.setFieldValue('labels', query);
      formik.setFieldValue('providers', query);
      formik.setFieldValue('salesChannels', query);
      formik.setFieldValue('earnings', query);
      formik.setFieldValue('tracks', query);
    }
  }, [settlementPeriod.data]);
  useEffect(() => {
    if (!!topTracks.data?.length) {
      formik.setFieldValue('track', topTracks.data[0]);
    }
  }, [topTracks.data]);

  const tokenData = useTokenData();
  const navigate = useNavigate();

  const isSectionLoading = (loading?: boolean) =>
    settlementPeriod.isLoading || loading;

  return (
    <TopbarPage topbar={<TopBar title='Statistics' hideBackButton />}>
      <FormikProvider value={formik}>
        <Form className='w-full h-full'>
          <StatisticsGrid>
            <StatisticsListContainer
              classname='statistics-home-label h-[320px]'
              loading={isSectionLoading(topLabels.isLoading)}
              headerTitle={
                <>
                  <PiIdentificationCard /> {t('statistics.top-labels')}
                </>
              }
              headerChildren={
                <div className='info-box-items flex p-4 w-full min-h-0 max-w-96'>
                  <Input.ComplexWrapper className='w-full'>
                    <FormInput.Select
                      label={t('input.label.start')}
                      name='labels.startSettlementPeriodId'
                      placeholder=''
                      options={options}
                      isClearable={false}
                    />
                    <FormInput.Select
                      label={t('input.label.end')}
                      name='labels.endSettlementPeriodId'
                      placeholder=''
                      options={options}
                      isClearable={false}
                    />
                  </Input.ComplexWrapper>
                </div>
              }
              label={item => item.name}
              value={item => formatCurrency(item.earnedAmount)}
              onItemClick={item => navigate(`/label/${item.id}`)}
              items={topLabels.data}
            />



            {tokenData?.StatisticsPermissions && (
              <>


                <Container className='max-w-full statistics-home-settlement'>
                  <Container.Header
                    title={
                      <>
                        <PiChartBar /> {t('statistics.settlement')}
                      </>
                    }>
                    <div className='info-box-items flex p-4 min-h-0 w-96'>
                      <Input.ComplexWrapper className='w-full'>
                        <FormInput.Select
                          label={t('input.label.start')}
                          name='earnings.startSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                        <FormInput.Select
                          label={t('input.label.end')}
                          name='earnings.endSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                      </Input.ComplexWrapper>
                    </div>
                  </Container.Header>
                  <Container.Content
                    loading={isSectionLoading(earnings.isLoading)}>
                    <div className='flex flex-col px-[150px] py-36 gap-2'>
                      <p className='opacity-50'>{t('table.total')} </p>
                      <p
                        className=' font-bold text-xxl'
                        style={{ color: 'var(--primary)' }}>
                        {formatCurrency(totalEarnings)}
                      </p>
                    </div>
                    <ResponsiveContainer width={'100%'} height={350}>
                      <BarChart
                        data={mappedEarnings}
                        margin={{
                          top: 0,
                          right: 100,
                          bottom: 20,
                        }}
                        barGap={2}
                        barCategoryGap={10}
                        maxBarSize={60}>
                        <CartesianGrid
                          strokeDasharray=''
                          vertical={false}
                          opacity={0.5}
                        />
                        <XAxis dataKey='name' stroke='var(--secondary)' />
                        <YAxis
                          width={150}
                          stroke='var(--secondary)'
                          tickFormatter={(value: any, index: number) =>
                            value === 0
                              ? ''
                              : formatCurrency(parseFloat(`${value}`))
                          }
                          tickCount={5}
                        />
                        <Tooltip
                          cursor={{ fill: 'none' }}
                          content={props => (
                            <div
                              className='flex  bg-opacity-95 rounded-sm p-8 gap-3'
                              style={{ backgroundColor: 'var(--background)' }}>
                              <p>{`${props.label}`}</p>
                              {props.payload && props.payload[0] && (
                                <>
                                  <p>-</p>
                                  <p style={{ color: props.payload[0].color }}>
                                    {formatCurrency(
                                      parseFloat(`${props.payload[0].value}`)
                                    )}
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        />
                        <ReferenceLine y={0} stroke='var(--secondary)' />
                        <Brush
                          dataKey='name'
                          height={20}
                          stroke='var(--primary)'
                        />
                        <Bar
                          dataKey='value'
                          fill='var(--primary)'
                          background={{ fill: 'var(--primary)', opacity: 0.2 }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Container.Content>
                </Container>

                <Container className='statistics-home-track '>
                  <Container.Header
                    title={
                      <div className=' flex w-full justify-between'>
                        <div className='flex gap-2'>
                          <PiVinylRecord /> {formik.values.track.title ?? '-'}
                        </div>
                      </div>
                    }>
                    <div className='info-box-items flex p-4 w-full min-h-0 max-w-96'>
                      <Button
                        isClear
                        onlyIcon
                        disabled={!formik.values.track.id}
                        onClick={() =>
                          window.open(`/track/${formik.values.track.id}`, '_blank')
                        }>
                        <Button.Icon
                          icon={<PiNavigationArrow transform='scale(-1,1)' />}
                        />
                      </Button>
                    </div>
                  </Container.Header>
                  <Container.Content
                    loading={isSectionLoading(trackEarnings.isLoading)}>
                    <ResponsiveContainer width={'100%'} height={350}>
                      <LineChart
                        data={mappedTrackEarnings}
                        margin={{
                          top: 20,
                          right: 100,
                          bottom: 20,
                        }}>
                        <CartesianGrid
                          strokeDasharray=''
                          vertical={false}
                          opacity={0.5}
                        />
                        <XAxis dataKey='name' stroke='var(--secondary)' />
                        <YAxis
                          width={150}
                          stroke='var(--secondary)'
                          tickFormatter={(value: any, index: number) =>
                            value === 0
                              ? ''
                              : formatCurrency(parseFloat(`${value}`))
                          }
                          tickCount={5}
                        />
                        <Tooltip
                          cursor={{ fill: 'none' }}
                          content={props => (
                            <div
                              className='flex  bg-opacity-95 rounded-sm p-8 gap-3'
                              style={{ backgroundColor: 'var(--background)' }}>
                              <p>{`${props.label}`}</p>
                              {props.payload && props.payload[0] && (
                                <>
                                  <p>-</p>
                                  <p style={{ color: props.payload[0].color }}>
                                    {formatCurrency(
                                      parseFloat(`${props.payload[0].value}`)
                                    )}
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        />
                        <ReferenceLine y={0} stroke='var(--secondary)' />

                        <Line
                          type='monotone'
                          dataKey='value'
                          fill='var(--primary)'
                          stroke='var(--primary)'
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Container.Content>
                </Container>
                <StatisticsListContainer
                  classname='statistics-home-tracks max-h-[428px]'
                  loading={isSectionLoading(topTracks.isLoading)}
                  headerTitle={
                    <>
                      <PiCalendar /> {t('statistics.top-tracks')}
                    </>
                  }
                  headerChildren={
                    <div className='info-box-items flex p-4 w-full min-h-0 max-w-96'>
                      <Input.ComplexWrapper className='w-full'>
                        <FormInput.Select
                          label={t('input.label.start')}
                          name='tracks.startSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                        <FormInput.Select
                          label={t('input.label.end')}
                          name='tracks.endSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                      </Input.ComplexWrapper>
                    </div>
                  }
                  isHighlighted={item => item.id === formik.values.track.id}
                  label={item => item.title}
                  value={item => formatCurrency(item.amountEarned)}
                  onItemClick={item => formik.setFieldValue('track', item)}
                  items={topTracks.data}
                />

                <Container className='statistics-home-sales-channels-chart'>
                  <Container.Header
                    title={
                      <div className=' flex w-full justify-between'>
                        <div className='flex gap-2'>
                          <PiVinylRecord /> {t('statistics.sales-channel')}
                        </div>
                      </div>
                    }>
                    <a href='#info'>
                      <Button wrapperClassNames='p-4' onlyIcon isClear>
                        <Button.Icon icon={<PiInfoBold />} />
                      </Button>
                    </a>
                  </Container.Header>
                  <Container.Content
                    loading={isSectionLoading(topSalesChannels.isLoading)}>
                    <ResponsiveContainer width={'100%'} height={500}>
                      <PieChart
                        margin={{
                          top: 20,
                          right: 100,
                          bottom: 20,
                        }}>
                        <Tooltip
                          cursor={{ fill: 'none' }}
                          content={props => (
                            <div
                              className='flex  bg-opacity-95 rounded-sm p-8 gap-3'
                              style={{ backgroundColor: 'var(--background)' }}>
                              {props.payload && props.payload[0] && (
                                <>
                                  <p>{`${props.payload[0].name}`}</p>
                                  <p>-</p>
                                  <p style={{ color: props.payload[0].color }}>
                                    {formatCurrency(
                                      parseFloat(`${props.payload[0].value}`)
                                    )}
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        />

                        <Pie
                          dataKey='allAmount'
                          data={mappedTopSalesChannels}
                          outerRadius={200}
                          fill='var(--primary)'
                          labelLine={{ width: 20 }}
                          label={props => (
                            <text
                              x={props.x}
                              y={props.y}
                              fill='var(--secondary)'
                              textAnchor={props.x > props.cx ? 'start' : 'end'}
                              className=''
                              dominantBaseline='central'>
                              {props.name}
                            </text>
                          )}>
                          {mappedTopSalesChannels?.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill='var(--primary)'
                              onClick={() =>
                                navigate(`/settings/sales-channel/${entry.id}`)
                              }
                              className='cursor-pointer'
                              fillOpacity={
                                (mappedTopSalesChannels.length - index) /
                                mappedTopSalesChannels.length
                              }
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Container.Content>
                </Container>
                <StatisticsListContainer
                  classname='statistics-home-sales-channels max-h-[578px]'
                  loading={isSectionLoading(topSalesChannels.isLoading)}
                  headerTitle={
                    <>
                      <PiCalendar /> {t('statistics.top-sales-channels')}
                    </>
                  }
                  headerChildren={
                    <div className='info-box-items flex p-4 w-full min-h-0 max-w-96'>
                      <Input.ComplexWrapper className='w-full'>
                        <FormInput.Select
                          label={t('input.label.start')}
                          name='salesChannels.startSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                        <FormInput.Select
                          label={t('input.label.end')}
                          name='salesChannels.endSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                      </Input.ComplexWrapper>
                    </div>
                  }
                  label={item => item.name}
                  value={item => formatCurrency(item.allAmount)}
                  onItemClick={item =>
                    navigate(`/settings/sales-channel/${item.id}`)
                  }
                  items={topSalesChannels.data?.slice(0, 10)}
                />
                <StatisticsListContainer
                  classname='statistics-home-providers max-h-[778px]'
                  loading={isSectionLoading(topProviders.isLoading)}
                  headerTitle={
                    <>
                      <PiVinylRecord /> {t('statistics.top-providers')}
                    </>
                  }
                  headerChildren={
                    <div className='info-box-items flex p-4 w-full min-h-0 max-w-96'>
                      <Input.ComplexWrapper className='w-full'>
                        <FormInput.Select
                          label={t('input.label.start')}
                          name='providers.startSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                        <FormInput.Select
                          label={t('input.label.end')}
                          name='providers.endSettlementPeriodId'
                          placeholder=''
                          options={options}
                          isClearable={false}
                        />
                      </Input.ComplexWrapper>
                    </div>
                  }
                  label={item => item.name}
                  value={item => formatCurrency(item.allAmount)}
                  onItemClick={item =>
                    navigate(`/settings/provider/${item.id}`)
                  }
                  items={topProviders.data?.slice(0, 10)}
                />
                <Container className='statistics-home-providers-chart'>
                  <Container.Header
                    title={
                      <div className=' flex w-full justify-between'>
                        <div className='flex gap-2'>
                          <PiVinylRecord /> {t('statistics.providers')}
                        </div>
                      </div>
                    }>
                    <a href='#info'>
                      <Button wrapperClassNames='p-4' onlyIcon isClear>
                        <Button.Icon icon={<PiInfoBold />} />
                      </Button>
                    </a>
                  </Container.Header>
                  <Container.Content
                    loading={isSectionLoading(
                      salesChannelsWithProviders.isLoading
                    )}>
                    <ResponsiveContainer width={'100%'} height={700}>
                      <RadarChart
                        data={mappedSalesChannelsWithProviders}
                        cx='50%'
                        cy='50%'
                        outerRadius='90%'
                        margin={{
                          top: 20,
                          right: 100,
                          bottom: 20,
                        }}>
                        <Tooltip
                          cursor={{ fill: 'none' }}
                          content={props => (
                            <div
                              className='flex flex-col  bg-opacity-95 rounded-sm p-8 gap-3 '
                              style={{ backgroundColor: 'var(--background)' }}
                              key={props.defaultIndex}>
                              {props.payload?.map(payload => (
                                <div className='flex  gap-3'>
                                  <p>{`${payload.name}`}</p>
                                  <p>-</p>
                                  <p style={{ color: payload.color }}>
                                    {formatCurrency(
                                      parseFloat(`${payload.value}`)
                                    )}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        />
                        <PolarGrid />
                        <PolarAngleAxis dataKey='name' />
                        <PolarRadiusAxis
                          angle={90 / mappedSalesChannelsWithProviders.length}
                          // domain={[0, 85000]}
                        />
                        {mappedSalesChannelsWithProviders[0] &&
                          mappedSalesChannelsWithProviders[0].providers.map(
                            (p, key) => (
                              <Radar
                                key={key}
                                name={p.providerName ?? '-'}
                                dataKey={p.providerId}
                                stroke='var(--primary)'
                                fill='var(--primary)'
                                fillOpacity={0.5}
                              />
                            )
                          )}
                      </RadarChart>
                    </ResponsiveContainer>
                  </Container.Content>
                </Container>
              </>
            )}
          </StatisticsGrid>
          {tokenData?.StatisticsPermissions && (
            <Container className='mt-3 max-w-full' id='info'>
              <Container.Header
                title={
                  <>
                    <PiInfoBold /> The statistics displayed in this application
                    are not sourced from the YouTube API but are derived from
                    internal data systems.
                  </>
                }
              />
            </Container>
          )}
        </Form>
      </FormikProvider>
    </TopbarPage>
  );
};

export default Statistics;
